/**
 * VISTOERA_TENANT_ADMIN_360_LIST_USAGE_ANALYTICS_DETAIL_BILLING_AI_USAGE_
 * AND_IMMUTABLE_CREDIT_ADJUSTMENT_P0_V1 — "Tenant 360 Detail" (task spec
 * §20-§50). Same canonical route (`/platform/tenants/:workspaceId`) as
 * before — no v2 route created (task spec §20).
 *
 * Existing Sites/Members/Entitlements/Domains/Plan History/Credit
 * Activity tabs are ALL preserved (task spec §23/§79 no-regression
 * rule), just reorganized into the new information architecture:
 *   概览 / 积分与用量 / 付费记录 / AI 使用 / 站点与域名 / 成员与权限 /
 *   资源与配额 / 操作记录
 * "Plan History" now lives as a secondary section inside 付费记录 (task
 * spec §38), and "Credit Activity" is now the Ledger table inside
 * 积分与用量 (task spec §27), both with their exact original columns
 * preserved, just re-hosted.
 *
 * All 1D/7D/30D/存储/到期日/最近活动 values shown here come straight off
 * `WorkspaceDetail` (== `GET /platform/workspaces/{id}`), which embeds
 * the EXACT same `tenant_usage_analytics_service` computation the Tenant
 * List row uses (task spec §51/§92-D7~D10) — never a second, parallel
 * calculation. Trend/breakdown/AI-usage/recent-activity additionally
 * come from the dedicated `/usage-analytics` call (kept separate per
 * task spec §73 — a heavier trend query should not block the header).
 */
import { CheckCircleOutlined, CopyOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { Alert, Button, Drawer, Empty, Segmented, Skeleton, Table, Tabs, Tag, Tooltip, Typography, message } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  platformGetEntitlements,
  platformGetWorkspace,
  platformGetWorkspaceAiUsage,
  platformGetWorkspacePaymentHistory,
  platformGetWorkspaceUsageAnalytics,
  platformListAllDomains,
  platformListAllSites,
  platformListAuditLog,
  platformListCreditLedgerAdmin,
  platformGetMemberAuthMatrix,
  platformListMembers,
  platformPutMemberAuthGrant,
  platformResetMemberAuthGrants,
  platformReverseCreditAdjustment,
} from '../api'
import type {
  AiUsageItem,
  BillingCreditLedgerEntry,
  DomainWithSite,
  EntitlementsMap,
  PaymentHistoryItem,
  PlatformAuditLogEntry,
  SiteWithWorkspace,
  TenantAnalytics,
  WorkspaceDetail,
  WorkspaceMember,
} from '../types'
import { SectionCard, SummaryCard, formatBytes, formatDateTime, formatRelativeTime, UsageBar } from './adminShared'
import CreditAdjustmentModal from './CreditAdjustmentModal'
import CreditBreakdownDonut from './CreditBreakdownDonut'
import CreditTrendChart from './CreditTrendChart'
import EntitlementsForm from './EntitlementsForm'
import MemberAuthMatrixPanel from './authMatrix/MemberAuthMatrixPanel'
import WorkspaceAuthMatrixPanel from './authMatrix/WorkspaceAuthMatrixPanel'

const PLATFORM_MEMBER_AUTH_ACTOR = {
  getMatrix: platformGetMemberAuthMatrix,
  putGrant: platformPutMemberAuthGrant,
  resetInherit: platformResetMemberAuthGrants,
}
import './platform-tables.css'
import PlanUpgradeModal from './PlanUpgradeModal'
import { cardIconBg, platformColors } from './theme'

const RISK_LABELS: Record<string, string> = {
  disabled: '已停用',
  expired: '套餐已到期',
  expiring_soon: '套餐即将到期',
  negative_balance: '积分为负',
  storage_near_quota: '存储接近上限',
}

function formatInt(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  const n = Number(v)
  if (Number.isNaN(n)) return String(v)
  return Math.trunc(n).toLocaleString('zh-CN')
}

function copyToClipboard(text: string) {
  navigator.clipboard
    ?.writeText(text)
    .then(() => message.success('已复制'))
    .catch(() => message.error('复制失败'))
}

function CopyableId({ label, value }: { label: string; value: string }) {
  return (
    <Tooltip title="点击复制">
      <span
        style={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: 12, color: platformColors.mutedText }}
        onClick={() => copyToClipboard(value)}
      >
        {label} <CopyOutlined style={{ fontSize: 10 }} />
      </span>
    </Tooltip>
  )
}

function ExpiryValue({ expiresAt, isPermanent }: { expiresAt: string | null; isPermanent: boolean }) {
  if (isPermanent || !expiresAt) return <span>永久</span>
  const dt = new Date(expiresAt)
  const daysLeft = Math.ceil((dt.getTime() - Date.now()) / 86400000)
  const dateLabel = Number.isNaN(dt.getTime()) ? expiresAt : dt.toISOString().slice(0, 10)
  const sub = daysLeft < 0 ? '已到期' : `还有 ${daysLeft} 天`
  const color = daysLeft < 0 ? platformColors.danger : daysLeft <= 7 ? platformColors.danger : daysLeft <= 30 ? platformColors.warning : undefined
  return (
    <span style={{ color }}>
      {dateLabel}（{sub}）
    </span>
  )
}

// ============================================================= Overview

function OverviewTab({
  workspace,
  analytics,
  analyticsLoading,
  days,
  onDaysChange,
  onViewAllAiUsage,
  onViewAllActivity,
}: {
  workspace: WorkspaceDetail
  analytics: TenantAnalytics | null
  analyticsLoading: boolean
  days: number
  onDaysChange: (d: number) => void
  onViewAllAiUsage: () => void
  onViewAllActivity: () => void
}) {
  const risk = workspace.risk_flags ?? []
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <SummaryCard
          title="积分余额"
          value={formatInt(workspace.credit_balance)}
          hint="可用积分"
          iconBg={cardIconBg.tenant}
          icon="💰"
        />
        <SummaryCard
          title="近1天消耗"
          value={formatInt(workspace.credit_usage_1d)}
          hint="相对滚动 24 小时"
          iconBg={cardIconBg.task}
          icon="📈"
        />
        <SummaryCard title="7日消耗" value={formatInt(workspace.credit_usage_7d)} hint="较前 7 天" iconBg={cardIconBg.task} icon="📊" />
        <SummaryCard title="30日消耗" value={formatInt(workspace.credit_usage_30d)} hint="较前 30 天" iconBg={cardIconBg.task} icon="🗓" />
        <SummaryCard
          title="存储使用"
          value={formatBytes(workspace.storage_used_bytes)}
          hint={`配额 ${formatBytes(workspace.storage_quota_bytes)}`}
          iconBg={cardIconBg.storage}
          icon="💾"
        />
        <SummaryCard
          title="最近活动"
          value={workspace.recent_activity_at ? formatRelativeTime(workspace.recent_activity_at) : '暂无活动'}
          hint={workspace.recent_activity_at ? formatDateTime(workspace.recent_activity_at) : undefined}
          iconBg={cardIconBg.worker}
          icon="🕐"
        />
        <SummaryCard
          title="套餐状态"
          value={workspace.is_permanent ? '永久' : workspace.expires_at ? formatRelativeTime(workspace.expires_at) : '—'}
          hint={workspace.is_permanent ? '无到期日' : workspace.expires_at ? formatDateTime(workspace.expires_at) : undefined}
          iconBg={cardIconBg.site}
          icon="🏷"
        />
        <SummaryCard
          title="风险提示"
          value={risk.length === 0 ? '一切正常' : `${risk.length} 项`}
          hint={risk.length === 0 ? undefined : risk.map((f) => RISK_LABELS[f] ?? f).join(' · ')}
          iconBg={risk.length === 0 ? cardIconBg.site : cardIconBg.domain}
          icon={risk.length === 0 ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
        />
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <div style={{ flex: '2 1 420px', minWidth: 360 }}>
          <SectionCard
            title="积分消耗趋势"
            extra={
              <Segmented
                size="small"
                value={days}
                onChange={(v) => onDaysChange(Number(v))}
                options={[
                  { label: '7 天', value: 7 },
                  { label: '30 天', value: 30 },
                  { label: '90 天', value: 90 },
                ]}
              />
            }
          >
            <div style={{ padding: 8 }}>
              {analyticsLoading || !analytics ? (
                <Skeleton active paragraph={{ rows: 4 }} />
              ) : (
                <CreditTrendChart
                  points={analytics.timeseries.map((p) => ({ date: p.date, consumed: Number(p.consumed) }))}
                  seriesLabel={`最近 ${days} 天的每日积分消耗`}
                  emptyLabel="暂无积分消耗记录"
                  formatDate={(d) => d.slice(5)}
                  formatValue={(v) => formatInt(v)}
                />
              )}
            </div>
          </SectionCard>
        </div>
        <div style={{ flex: '1 1 300px', minWidth: 280 }}>
          <SectionCard title={`积分消耗分布（最近 ${days} 天）`}>
            <div style={{ padding: 8 }}>
              {analyticsLoading || !analytics ? (
                <Skeleton active paragraph={{ rows: 4 }} />
              ) : (
                <CreditBreakdownDonut
                  slices={analytics.breakdown.map((s) => ({ label: s.label, amount: Number(s.amount), percent: s.percent }))}
                  totalLabel="总消耗"
                  emptyLabel="暂无积分消耗记录"
                  formatAmount={(v) => formatInt(v)}
                />
              )}
            </div>
          </SectionCard>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 420px', minWidth: 360 }}>
          <SectionCard title="最近的 AI 使用记录" extra={<a onClick={onViewAllAiUsage}>查看全部 →</a>} allowOverflow>
            {analyticsLoading || !analytics ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : analytics.recent_ai_usage.length === 0 ? (
              <Empty description="暂无 AI 使用记录" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Table
                className="pf-data-table"
                size="small"
                rowKey={(_, i) => String(i)}
                pagination={false}
                dataSource={analytics.recent_ai_usage}
                columns={[
                  { title: '时间', dataIndex: 'created_at', render: (v: string) => formatRelativeTime(v) },
                  { title: '功能节点', dataIndex: 'operation_label' },
                  { title: '模型', dataIndex: 'model_label' },
                  {
                    title: '用量',
                    key: 'usage',
                    render: (_: unknown, r) => (r.usage_quantity ? `${formatInt(r.usage_quantity)} ${r.billing_unit ?? ''}` : '—'),
                  },
                  { title: '积分消耗', dataIndex: 'credit_amount', align: 'right' as const, render: (v: string) => formatInt(v) },
                ]}
              />
            )}
          </SectionCard>
        </div>
        <div style={{ flex: '1 1 320px', minWidth: 300 }}>
          <SectionCard title="近期活动" extra={<a onClick={onViewAllActivity}>查看全部 →</a>} allowOverflow>
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
              {analyticsLoading || !analytics ? (
                <Skeleton active paragraph={{ rows: 4 }} />
              ) : analytics.recent_activity.length === 0 ? (
                <Empty description="暂无活动记录" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '4px 8px' }}>
                  {analytics.recent_activity.map((ev, i) => (
                    <div key={i} style={{ fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span>{ev.label}</span>
                      <span style={{ color: platformColors.mutedText, whiteSpace: 'nowrap' }}>{formatRelativeTime(ev.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  )
}

// ============================================================= Credits & Usage

const LEDGER_TXN_LABELS: Record<string, string> = {
  admin_adjustment: '管理员调整',
  reversal: '冲正',
  usage_charge: '功能消耗',
  plan_upgrade_grant: '套餐赠送',
  topup_purchase: '充值到账',
  promotion_bonus: '活动赠送',
  daily_reward_claim: '每日签到',
  refund: '退款',
}

function CreditsUsageTab({
  workspace,
  analytics,
  analyticsLoading,
  days,
  onDaysChange,
  onAdjust,
  onReload,
}: {
  workspace: WorkspaceDetail
  analytics: TenantAnalytics | null
  analyticsLoading: boolean
  days: number
  onDaysChange: (d: number) => void
  onAdjust: () => void
  onReload: () => void
}) {
  const [ledger, setLedger] = useState<BillingCreditLedgerEntry[]>([])
  const [ledgerTotal, setLedgerTotal] = useState(0)
  const [ledgerPage, setLedgerPage] = useState(1)
  const [ledgerLoading, setLedgerLoading] = useState(true)
  const [reversingId, setReversingId] = useState<string | null>(null)
  const pageSize = 20

  const loadLedger = useCallback(() => {
    setLedgerLoading(true)
    platformListCreditLedgerAdmin({ workspace_id: workspace.id, limit: pageSize, offset: (ledgerPage - 1) * pageSize })
      .then((r) => {
        setLedger(r.data.items)
        setLedgerTotal(r.data.total)
      })
      .finally(() => setLedgerLoading(false))
  }, [workspace.id, ledgerPage])

  useEffect(loadLedger, [loadLedger])

  const onReverse = (row: BillingCreditLedgerEntry) => {
    const reversalNote = window.prompt('请输入冲正原因（将写入永久记录）', 'Correcting a mistaken admin adjustment')
    if (reversalNote === null) return
    if (!reversalNote.trim()) {
      message.error('冲正原因为必填项')
      return
    }
    setReversingId(row.id)
    platformReverseCreditAdjustment(workspace.id, row.id, {
      note: reversalNote.trim(),
      reversal_request_id: `rev-${row.id}-${Date.now()}`,
    })
      .then(() => {
        message.success('已创建冲正记录，原记录永久保留')
        loadLedger()
        onReload()
      })
      .catch((err) => message.error((err as Error).message))
      .finally(() => setReversingId(null))
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <SummaryCard title="当前余额" value={formatInt(workspace.credit_balance)} iconBg={cardIconBg.tenant} icon="💰" />
        <SummaryCard title="近1天消耗" value={formatInt(workspace.credit_usage_1d)} iconBg={cardIconBg.task} icon="📈" />
        <SummaryCard title="7日消耗" value={formatInt(workspace.credit_usage_7d)} iconBg={cardIconBg.task} icon="📊" />
        <SummaryCard title="30日消耗" value={formatInt(workspace.credit_usage_30d)} iconBg={cardIconBg.task} icon="🗓" />
      </div>

      <SectionCard
        title="历史趋势"
        extra={
          <Segmented
            size="small"
            value={days}
            onChange={(v) => onDaysChange(Number(v))}
            options={[
              { label: '7 天', value: 7 },
              { label: '30 天', value: 30 },
              { label: '90 天', value: 90 },
            ]}
          />
        }
      >
        <div style={{ padding: 8 }}>
          {analyticsLoading || !analytics ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : (
            <CreditTrendChart
              points={analytics.timeseries.map((p) => ({ date: p.date, consumed: Number(p.consumed) }))}
              seriesLabel={`最近 ${days} 天的每日积分消耗`}
              emptyLabel="暂无积分消耗记录"
              formatDate={(d) => d.slice(5)}
              formatValue={(v) => formatInt(v)}
            />
          )}
        </div>
      </SectionCard>

      <SectionCard title="积分明细（Credit Ledger）" extra={<Button onClick={onAdjust}>调整积分</Button>} allowOverflow>
        <Table
          className="pf-data-table"
          rowKey="id"
          loading={ledgerLoading}
          dataSource={ledger}
          sticky
          scroll={{ x: true }}
          pagination={{
            current: ledgerPage,
            pageSize,
            total: ledgerTotal,
            onChange: setLedgerPage,
            showTotal: (t) => `共 ${t} 条记录`,
          }}
          columns={[
            { title: '时间', dataIndex: 'created_at', render: (v: string) => formatDateTime(v) },
            {
              title: '类型',
              dataIndex: 'transaction_type',
              render: (v: string) => <Tag>{LEDGER_TXN_LABELS[v] ?? v}</Tag>,
            },
            {
              title: '数量',
              dataIndex: 'amount',
              align: 'right' as const,
              render: (v: string, row) => (
                <span style={{ color: row.direction === 'credit' ? platformColors.success : platformColors.danger }}>
                  {row.direction === 'credit' ? '+' : '-'}
                  {formatInt(v)}
                </span>
              ),
            },
            { title: '调整前余额', dataIndex: 'balance_before', align: 'right' as const, render: (v: string) => formatInt(v) },
            { title: '调整后余额', dataIndex: 'balance_after', align: 'right' as const, render: (v: string) => formatInt(v) },
            { title: '来源', dataIndex: 'source_type', render: (v: string | null) => v ?? '—' },
            {
              title: '备注',
              key: 'note',
              render: (_: unknown, row) => row.admin_note || row.description || '—',
            },
            { title: '管理员/系统', dataIndex: 'actor', render: (v: string | null) => v ?? '系统' },
            { title: 'Reference', key: 'ref', render: (_: unknown, row) => <CopyableId label={row.id.slice(0, 8)} value={row.id} /> },
            {
              title: '状态',
              key: 'reversal_status',
              render: (_: unknown, row) => {
                if (row.reversed_by_entry_id) return <Tag color="default">已冲正</Tag>
                if (row.reverses_entry_id) return <Tag color="purple">冲正记录</Tag>
                return null
              },
            },
            {
              title: '操作',
              key: 'actions',
              fixed: 'right' as const,
              render: (_: unknown, row) =>
                row.is_reversible ? (
                  <Button size="small" danger loading={reversingId === row.id} onClick={() => onReverse(row)}>
                    冲正
                  </Button>
                ) : null,
            },
          ]}
        />
      </SectionCard>
    </div>
  )
}

// ============================================================= Payment History

function PaymentHistoryTab({ workspace }: { workspace: WorkspaceDetail }) {
  const [items, setItems] = useState<PaymentHistoryItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    platformGetWorkspacePaymentHistory(workspace.id)
      .then((r) => setItems(r.data.items))
      .catch((err) => setError(err.message))
  }, [workspace.id])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {error && <Alert type="error" message={error} showIcon />}
      <SectionCard title="付费记录" allowOverflow>
        {items === null ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : items.length === 0 ? (
          <Empty description="暂无记录" style={{ padding: '32px 0' }} />
        ) : (
          <Table
            className="pf-data-table"
            rowKey="id"
            dataSource={items}
            sticky
            columns={[
              { title: '时间', dataIndex: 'created_at', render: (v: string) => formatDateTime(v) },
              { title: '类型', dataIndex: 'type_label' },
              {
                title: '数额',
                dataIndex: 'amount',
                align: 'right' as const,
                render: (v: string, row) => `${row.direction === 'credit' ? '+' : '-'}${formatInt(v)}`,
              },
              { title: '备注', dataIndex: 'description', render: (v: string | null) => v ?? '—' },
              { title: 'Reference', dataIndex: 'source_id', render: (v: string | null) => (v ? v.slice(0, 8) : '—') },
            ]}
          />
        )}
      </SectionCard>

      <SectionCard title="套餐历史" allowOverflow>
        <Table
          className="pf-data-table"
          rowKey="id"
          dataSource={workspace.plan_history}
          sticky
          columns={[
            { title: '时间', dataIndex: 'created_at', render: (v: string) => formatDateTime(v) },
            { title: '来源', dataIndex: 'source' },
            { title: '原套餐', dataIndex: 'from_plan_key', render: (v: string | null) => v ?? '（首次分配）' },
            { title: '新套餐', dataIndex: 'to_plan_key' },
            { title: '赠送积分', dataIndex: 'credit_grant_applied', align: 'right' as const, render: (v: string) => formatInt(v) },
            {
              title: '存储变化',
              key: 'storage_change',
              render: (_: unknown, row) =>
                row.storage_before_bytes != null && row.storage_after_bytes != null
                  ? `${formatBytes(row.storage_before_bytes)} → ${formatBytes(row.storage_after_bytes)}`
                  : '—',
            },
            { title: '操作人', dataIndex: 'actor', render: (v: string | null) => v ?? '系统' },
          ]}
        />
      </SectionCard>
    </div>
  )
}

// ============================================================= AI Usage

function AiUsageTab({ workspaceId }: { workspaceId: string }) {
  const [days, setDays] = useState(30);
  const [items, setItems] = useState<AiUsageItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const pageSize = 20

  useEffect(() => {
    setLoading(true)
    platformGetWorkspaceAiUsage(workspaceId, { days, page, page_size: pageSize })
      .then((r) => {
        setItems(r.data.items)
        setTotal(r.data.total)
      })
      .finally(() => setLoading(false))
  }, [workspaceId, days, page])

  return (
    <SectionCard
      title="AI 使用"
      extra={
        <Segmented
          size="small"
          value={days}
          onChange={(v) => {
            setDays(Number(v))
            setPage(1)
          }}
          options={[
            { label: '1天', value: 1 },
            { label: '7天', value: 7 },
            { label: '30天', value: 30 },
            { label: '90天', value: 90 },
          ]}
        />
      }
      allowOverflow
    >
      {!loading && total === 0 ? (
        <Empty description="暂无 AI 使用记录" style={{ padding: '32px 0' }} />
      ) : (
        <Table
          className="pf-data-table"
          rowKey={(r) => `${r.operation_key}-${r.provider_key}-${r.model_key}-${r.billing_unit}`}
          loading={loading}
          dataSource={items}
          sticky
          scroll={{ x: true }}
          pagination={{ current: page, pageSize, total, onChange: setPage, showTotal: (t) => `共 ${t} 条` }}
          columns={[
            { title: 'Provider', dataIndex: 'provider_label' },
            { title: 'Model', dataIndex: 'model_label' },
            { title: '功能节点', dataIndex: 'operation_label' },
            { title: '调用次数', dataIndex: 'call_count', align: 'right' as const, render: (v: number) => v.toLocaleString('zh-CN') },
            {
              title: '用量',
              key: 'usage',
              align: 'right' as const,
              render: (_: unknown, r) => `${formatInt(r.usage_quantity)} ${r.billing_unit ?? ''}`,
            },
            { title: '积分消耗', dataIndex: 'credit_amount', align: 'right' as const, render: (v: string) => formatInt(v) },
            { title: '占比', dataIndex: 'percent', align: 'right' as const, render: (v: number) => `${v.toFixed(1)}%` },
            {
              title: '最后使用时间',
              dataIndex: 'last_used_at',
              render: (v: string | null) => (v ? formatRelativeTime(v) : '—'),
            },
          ]}
        />
      )}
    </SectionCard>
  )
}

// ============================================================= Audit Log

function AuditLogTab({ workspaceId }: { workspaceId: string }) {
  const [items, setItems] = useState<PlatformAuditLogEntry[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const pageSize = 20

  useEffect(() => {
    setLoading(true)
    platformListAuditLog({ workspace_id: workspaceId, page, page_size: pageSize })
      .then((r) => {
        setItems(r.data.items)
        setTotal(r.data.total)
      })
      .finally(() => setLoading(false))
  }, [workspaceId, page])

  return (
    <SectionCard title="操作记录" allowOverflow>
      {!loading && total === 0 ? (
        <Empty description="暂无操作记录" style={{ padding: '32px 0' }} />
      ) : (
        <Table
          className="pf-data-table"
          rowKey="id"
          loading={loading}
          dataSource={items}
          sticky
          pagination={{ current: page, pageSize, total, onChange: setPage, showTotal: (t) => `共 ${t} 条` }}
          columns={[
            { title: '时间', dataIndex: 'created_at', render: (v: string | null) => formatDateTime(v) },
            { title: '操作', dataIndex: 'action' },
            { title: '管理员', dataIndex: 'actor_email', render: (v: string | null) => v ?? '—' },
            { title: '结果', dataIndex: 'result', render: (v: string) => <Tag color={v === 'success' ? 'green' : 'red'}>{v}</Tag> },
            { title: '详情', dataIndex: 'detail', render: (v: string | null) => v ?? '—' },
          ]}
        />
      )}
    </SectionCard>
  )
}

// ============================================================= root page

export default function PlatformTenantDetailPage() {
  const { workspaceId = '' } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [workspace, setWorkspace] = useState<WorkspaceDetail | null>(null)
  const [members, setMembers] = useState<WorkspaceMember[] | null>(null)
  const [memberAuthTarget, setMemberAuthTarget] = useState<WorkspaceMember | null>(null)
  const [entitlements, setEntitlements] = useState<EntitlementsMap | null>(null)
  const [sites, setSites] = useState<SiteWithWorkspace[] | null>(null)
  const [domains, setDomains] = useState<DomainWithSite[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [adjustOpen, setAdjustOpen] = useState(false)

  const [analytics, setAnalytics] = useState<TenantAnalytics | null>(null)
  const [analyticsLoading, setAnalyticsLoading] = useState(true)
  const [days, setDays] = useState(30)

  const activeTab = searchParams.get('tab') || 'overview'
  const setActiveTab = (tab: string) => setSearchParams((prev) => new URLSearchParams({ ...Object.fromEntries(prev), tab }))

  const load = useCallback(() => {
    Promise.all([
      platformGetWorkspace(workspaceId),
      platformListMembers(workspaceId),
      platformGetEntitlements(workspaceId),
      platformListAllSites(),
      platformListAllDomains(),
    ])
      .then(([w, m, e, s, d]) => {
        setWorkspace(w.data)
        setMembers(m.data)
        setEntitlements(e.data.entitlements)
        setSites(s.data.filter((site) => site.workspace_id === workspaceId))
        setDomains(d.data.filter((dom) => dom.workspace_id === workspaceId))
      })
      .catch((err) => setError(err.message))
  }, [workspaceId])

  useEffect(load, [load])

  const loadAnalytics = useCallback(() => {
    setAnalyticsLoading(true)
    platformGetWorkspaceUsageAnalytics(workspaceId, days)
      .then((r) => setAnalytics(r.data))
      .finally(() => setAnalyticsLoading(false))
  }, [workspaceId, days])

  useEffect(loadAnalytics, [loadAnalytics])

  const memberCount = members?.length ?? workspace?.member_count ?? 0
  const siteAndDomains = useMemo(
    () => ({ sites: sites ?? [], domains: domains ?? [] }),
    [sites, domains],
  )

  if (error) return <Alert type="error" message={error} showIcon />
  if (!workspace) return <Skeleton active />

  return (
    <div>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        <a onClick={() => navigate('/platform/tenants')}>租户管理 / 租户列表</a> / 租户详情
      </Typography.Text>

      {/* Tenant Header — task spec §21/§22. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          margin: '8px 0 16px',
        }}
      >
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: cardIconBg.tenant,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              fontWeight: 700,
              color: platformColors.primaryText,
              flexShrink: 0,
            }}
          >
            {workspace.name.slice(0, 1).toUpperCase()}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {workspace.name}
              </Typography.Title>
              <Tag color={workspace.status === 'active' ? 'green' : 'default'}>{workspace.status}</Tag>
              <Tag color="purple" data-testid="tenant-detail-plan">
                {workspace.plan_display_name
                  ? workspace.plan_display_name
                  : workspace.plan_key
                    ? '套餐异常'
                    : '未配置'}
              </Tag>
              {workspace.plan_enabled === false ? (
                <Tag style={{ fontSize: 11, lineHeight: '16px' }}>已停用</Tag>
              ) : null}
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
              <CopyableId label={`Slug ${workspace.slug}`} value={workspace.slug} />
              <CopyableId label={`Workspace ID ${workspace.id.slice(0, 8)}`} value={workspace.id} />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            onClick={() => setUpgradeOpen(true)}
            disabled={workspace.plan_key === 'premium'}
            data-testid="upgrade-plan-btn"
          >
            升级套餐
          </Button>
          <Button type="primary" onClick={() => setAdjustOpen(true)} data-testid="adjust-credit-btn">
            调整积分
          </Button>
        </div>
      </div>

      {/* Compact header info strip. */}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 16, fontSize: 13 }}>
        <div>
          <div style={{ color: platformColors.mutedText, fontSize: 12 }}>管理员邮箱</div>
          <div>{workspace.owner_email ?? '未知'}</div>
        </div>
        <div>
          <div style={{ color: platformColors.mutedText, fontSize: 12 }}>账号注册</div>
          <div>{workspace.owner_registered_at ? formatDateTime(workspace.owner_registered_at) : '—'}</div>
        </div>
        <div>
          <div style={{ color: platformColors.mutedText, fontSize: 12 }}>Workspace 创建</div>
          <div>{formatDateTime(workspace.created_at)}</div>
        </div>
        <div>
          <div style={{ color: platformColors.mutedText, fontSize: 12 }}>到期日期</div>
          <div>
            <ExpiryValue expiresAt={workspace.expires_at} isPermanent={workspace.is_permanent} />
          </div>
        </div>
        <div>
          <div style={{ color: platformColors.mutedText, fontSize: 12 }}>站点 / 成员 / 域名</div>
          <div>
            {workspace.site_count} / {memberCount} / {workspace.domain_count}
          </div>
        </div>
        <div>
          <div style={{ color: platformColors.mutedText, fontSize: 12 }}>存储</div>
          <div style={{ minWidth: 160 }}>
            {formatBytes(workspace.storage_used_bytes)} / {formatBytes(workspace.storage_quota_bytes)}
            <UsageBar used={workspace.storage_used_bytes} total={workspace.storage_quota_bytes} />
          </div>
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'overview',
            label: '概览',
            children: (
              <OverviewTab
                workspace={workspace}
                analytics={analytics}
                analyticsLoading={analyticsLoading}
                days={days}
                onDaysChange={setDays}
                onViewAllAiUsage={() => setActiveTab('ai-usage')}
                onViewAllActivity={() => setActiveTab('audit')}
              />
            ),
          },
          {
            key: 'credits',
            label: '积分与用量',
            children: (
              <CreditsUsageTab
                workspace={workspace}
                analytics={analytics}
                analyticsLoading={analyticsLoading}
                days={days}
                onDaysChange={setDays}
                onAdjust={() => setAdjustOpen(true)}
                onReload={load}
              />
            ),
          },
          {
            key: 'payments',
            label: '付费记录',
            children: <PaymentHistoryTab workspace={workspace} />,
          },
          {
            key: 'ai-usage',
            label: 'AI 使用',
            children: <AiUsageTab workspaceId={workspaceId} />,
          },
          {
            key: 'sites-domains',
            label: '站点与域名',
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <SectionCard title="站点" allowOverflow>
                  <Table
                    className="pf-data-table"
                    rowKey="id"
                    loading={!sites}
                    dataSource={siteAndDomains.sites}
                    sticky
                    columns={[
                      { title: 'Name', dataIndex: 'name', align: 'left' as const },
                      { title: 'Slug', dataIndex: 'slug', align: 'left' as const },
                      { title: 'Status', dataIndex: 'status', render: (s: string) => <Tag>{s}</Tag> },
                      { title: 'Default Locale', dataIndex: 'default_locale' },
                    ]}
                  />
                </SectionCard>
                <SectionCard title="域名" allowOverflow>
                  <Table
                    className="pf-data-table"
                    rowKey="id"
                    loading={!domains}
                    dataSource={siteAndDomains.domains}
                    sticky
                    columns={[
                      { title: 'Hostname', dataIndex: 'hostname', align: 'left' as const },
                      { title: 'Site', dataIndex: 'site_name', align: 'left' as const },
                      { title: 'Primary', dataIndex: 'is_primary', render: (v: boolean) => (v ? <Tag color="gold">Primary</Tag> : null) },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        render: (s: string) => <Tag color={s === 'active' ? 'green' : s === 'error' ? 'red' : 'orange'}>{s}</Tag>,
                      },
                    ]}
                  />
                </SectionCard>
              </div>
            ),
          },
          {
            key: 'members',
            label: '成员与权限',
            children: (
              <SectionCard title="成员" allowOverflow>
                <Table
                  className="pf-data-table"
                  rowKey="user_id"
                  loading={!members}
                  dataSource={members ?? []}
                  sticky
                  columns={[
                    { title: 'Email', dataIndex: 'email', align: 'left' as const },
                    { title: 'Display Name', dataIndex: 'display_name', align: 'left' as const },
                    { title: 'Role', dataIndex: 'role', render: (r: string) => <Tag color="blue">{r}</Tag> },
                    { title: 'Status', dataIndex: 'status' },
                    {
                      title: '功能授权',
                      key: 'member_auth',
                      width: 120,
                      render: (_: unknown, row: WorkspaceMember) => (
                        <Button
                          type="link"
                          size="small"
                          disabled={row.status !== 'active'}
                          onClick={() => setMemberAuthTarget(row)}
                          data-testid={`platform-member-auth-${row.user_id}`}
                        >
                          功能授权
                        </Button>
                      ),
                    },
                  ]}
                />
                <Drawer
                  title={
                    memberAuthTarget
                      ? `功能授权 · ${memberAuthTarget.display_name || memberAuthTarget.email}`
                      : '功能授权'
                  }
                  open={!!memberAuthTarget}
                  onClose={() => setMemberAuthTarget(null)}
                  width={960}
                  destroyOnClose
                >
                  {memberAuthTarget ? (
                    <MemberAuthMatrixPanel
                      workspaceId={workspaceId}
                      planKey={workspace.plan_key}
                      planLabel={workspace.plan_display_name || workspace.plan_key}
                      members={(members ?? [])
                        .filter((m) => m.status === 'active')
                        .map((m) => ({
                          user_id: m.user_id,
                          display_name: m.display_name || m.email,
                          email: m.email,
                          role: m.role,
                          role_label: m.role,
                        }))}
                      initialUserId={memberAuthTarget.user_id}
                      hideMemberPicker
                      actor={PLATFORM_MEMBER_AUTH_ACTOR}
                    />
                  ) : null}
                </Drawer>
              </SectionCard>
            ),
          },
          {
            // VistoeraTASK-00637 — Workspace node/channel override matrix (not Entitlements).
            key: 'function-auth',
            label: '功能授权',
            children: (
              <WorkspaceAuthMatrixPanel
                workspaceId={workspaceId}
                workspaceName={workspace.name}
                planKey={workspace.plan_key}
                planDisplayName={workspace.plan_display_name}
              />
            ),
          },
          {
            key: 'entitlements',
            label: '资源与配额',
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <SectionCard title="存储配额">
                  <div style={{ padding: 8, maxWidth: 420 }}>
                    <div>
                      {formatBytes(workspace.storage_used_bytes)} / {formatBytes(workspace.storage_quota_bytes)}
                    </div>
                    <UsageBar used={workspace.storage_used_bytes} total={workspace.storage_quota_bytes} />
                  </div>
                </SectionCard>
                <SectionCard title="功能与配额（Entitlements）">
                  <div style={{ padding: 8 }}>
                    {entitlements ? (
                      <EntitlementsForm workspaceId={workspaceId} entitlements={entitlements} onSaved={setEntitlements} />
                    ) : (
                      <Skeleton active />
                    )}
                  </div>
                </SectionCard>
              </div>
            ),
          },
          {
            key: 'audit',
            label: '操作记录',
            children: <AuditLogTab workspaceId={workspaceId} />,
          },
        ]}
      />

      <PlanUpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        workspaceId={workspace.id}
        workspaceName={workspace.name}
        currentPlanKey={workspace.plan_key}
        currentPlanDisplayName={workspace.plan_display_name}
        creditBalance={workspace.credit_balance}
        storageQuotaBytes={workspace.storage_quota_bytes}
        onSuccess={load}
      />
      <CreditAdjustmentModal
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        workspaceId={workspace.id}
        workspaceName={workspace.name}
        creditBalance={workspace.credit_balance}
        onSuccess={() => {
          load()
          loadAnalytics()
        }}
      />
    </div>
  )
}
